import React, { useMemo } from "react";
import { graphql } from "gatsby";
import { PriceAbs, PricePerc } from "../molecules/product-price";
import Icon from "../atoms/icon";
import ReactTooltip from "react-tooltip";
import HTMLReactParser from "html-react-parser";
import { formatPrice } from "../../utility/prices";
import classNames from "classnames";

const CATEGORIES_BY_ID = {
  2955737: "High",
  2955743: "Medium",
};
const CATEGORIES_BY_COLOR = {
  red: "high",
  white: "default",
  blue: "medium",
};

const Discount = ({
  price,
  priceLanguage,
  priceAbs,
  labelManagerLabels,
  config,
  contributoRaee,
  className,
  style,
  color,
}) => {
  const omnibusNotFormatted = price?.omnibusPrice?.[0]?.value
    ? JSON?.parse(price?.omnibusPrice?.[0]?.value)?.amount
    : null;

  return !priceAbs ? (
    <span
      className={classNames(
        "discount_calc discount_calc--tag",
        className,
        color !== "Red" && "discount_calc--tag-white",
        (color === "Red" || color === "Blue") && "discount_calc--price-color"
      )}
      style={style}
    >
      {config?.tagTextPosition === "Before value" && labelManagerLabels?.save && (
        <span>{labelManagerLabels?.save}&nbsp;</span>
      )}
      <span>
        <PricePerc
          priceLanguage={priceLanguage}
          priceData={price}
          contributoRaee={contributoRaee}
          priceWithWeee={config.priceWithWeee}
          omnibusPrice={
            omnibusNotFormatted && omnibusNotFormatted > price?.sellingPrice?.value
              ? omnibusNotFormatted
              : null
          }
          save={labelManagerLabels?.save && true}
          origin="tag"
        />
      </span>
      {config?.tagTextPosition === "After value" && labelManagerLabels?.save && (
        <span>&nbsp;{labelManagerLabels?.save}</span>
      )}
      {labelManagerLabels?.pricePercentageTooltip && (
        <>
          <p className="product-detail__priceInfo" data-for="productPriceInfoTag" data-tip>
            <Icon name="symbols-info-price" />
          </p>
          <ReactTooltip
            id="productPriceInfoTag"
            place="bottom"
            effect="solid"
            textColor="#0a2240"
            type="light"
          >
            <Icon name="symbols-info-price" />
            {HTMLReactParser(labelManagerLabels?.pricePercentageTooltip)}
          </ReactTooltip>
        </>
      )}
    </span>
  ) : (
    <span
      className={classNames(
        "discount_calc discount_calc--tag",
        className,
        color !== "Red" && "discount_calc--tag-white",
        (color === "Red" || color === "Blue") && "discount_calc--price-color"
      )}
      style={style}
    >
      {config?.tagTextPosition === "Before value" && labelManagerLabels?.save && (
        <span>{labelManagerLabels?.save}&nbsp;</span>
      )}
      <span>
        <PriceAbs
          priceLanguage={priceLanguage}
          priceData={price}
          currency={config.currency}
          omnibusPrice={
            omnibusNotFormatted && omnibusNotFormatted > price?.sellingPrice?.value
              ? omnibusNotFormatted
              : null
          }
          save={labelManagerLabels?.save && true}
          origin="tag"
        />
      </span>
      {config?.tagTextPosition === "After value" && labelManagerLabels?.save && (
        <span>&nbsp;{labelManagerLabels?.save}</span>
      )}
    </span>
  );
};

const ProductTag = ({ data, config, labelManagerLabels, siteMetadata }) => {
  const { label, category } = useMemo(() => {
    const { tag, color, liferayFields } = data;
    const colorCategory = color?.length ? CATEGORIES_BY_COLOR[color[0]] : null;
    const taxonomyCategory = liferayFields?.taxonomyCategoryBriefs?.find(
      (category) => category.parentTaxonomyVocabulary?.name === "product-tag"
    );
    const colorTaxonomy = taxonomyCategory
      ? CATEGORIES_BY_ID[
          taxonomyCategory.parentTaxonomyCategory
            ? taxonomyCategory.parentTaxonomyCategory.id
            : taxonomyCategory.id
        ]
      : null;
    return {
      label: tag || taxonomyCategory?.name,
      category: (tag ? colorCategory : colorTaxonomy) || "default",
    };
  }, [data]);

  const priceAbs = config?.tagvalue === "Absolute";

  const contributoRaeeMulti = useMemo(
    () =>
      data?.price?.contributoRaee?.[0]?.value
        ? formatPrice(
            JSON.parse(data?.price.contributoRaee?.[0].value)?.amount,
            data?.price.sellingPrice?.currency || config.currency,
            siteMetadata.priceLanguage
          )
        : null,
    [data?.price, siteMetadata.priceLanguage, config.currency]
  );

  const color = data?.price ? config?.tagColor : data?.colorHexa ? data?.colorHexa : null;

  return data?.price ? (
    <Discount
      price={data.price}
      priceLanguage={siteMetadata.priceLanguage}
      priceAbs={priceAbs}
      labelManagerLabels={labelManagerLabels}
      config={config}
      contributoRaee={contributoRaeeMulti}
      className={`product-card__tag product-card__tag--${category}`}
      style={{
        backgroundColor: color === "Blue" ? "#0a2240" : color === "Red" ? "#dd052b" : color,
        color: data?.colorHexaText
          ? data?.colorHexaText
          : color === "Blue" || color === "Red"
          ? "#fff"
          : null,
      }}
      color={color}
    />
  ) : (
    <span
      style={{
        backgroundColor: color === "Blue" ? "#0a2240" : color === "Red" ? "#dd052b" : color,
        color: data?.colorHexaText
          ? data?.colorHexaText
          : color === "Blue" || color === "Red"
          ? "#fff"
          : null,
      }}
      className={`product-card__tag product-card__tag--${category}`}
    >
      <span>{label}</span>
    </span>
  );
};

export default ProductTag;
export const fragment = graphql`
  fragment ProductTagFragment on LiferayProductTag {
    id
    tag
    color
    skus
    lines
    colorHexa
    colorHexaText
    liferayFields {
      taxonomyCategoryBriefs {
        liferayId
        name
        parentTaxonomyVocabulary {
          name
        }
        parentTaxonomyCategory {
          name
          id
        }
      }
    }
  }
`;
